<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed },
      { 'collapsed': isVerticalMenuCollapsed },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
  >
    <!-- main menu header-->
    <div class="navbar-header">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <div class="navbar-header-content">
          <!-- Logo centered -->
          <b-link
            class="navbar-brand"
            to="/"
          >
            <span class="brand-logo">
              <logo-full />
            </span>
          </b-link>

          <!-- Mobile close button -->
          <button
            class="sidebar-close-btn d-xl-none"
            @click="toggleVerticalMenuActive"
          >
            <feather-icon
              icon="XIcon"
              size="25"
            />
          </button>
        </div>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- Sidebar body wrapper for flex layout -->
    <div class="sidebar-body">
      <!-- main menu content-->
      <vue-perfect-scrollbar
        :settings="perfectScrollbarSettings"
        class="main-menu-content scroll-area"
        tagname="ul"
        @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
      >
        <vertical-nav-menu-items
          :items="navItems"
          class="navigation navigation-main"
        />
      </vue-perfect-scrollbar>
      <!-- /main menu content-->

      <!-- Sidebar footer -->
      <div class="sidebar-footer">
        <!-- User actions section -->
        <div class="sidebar-user-actions">
          <router-link
            :to="{ name: 'profile' }"
            class="user-action-link"
            :title="isVerticalMenuCollapsed ? $t('DEBTIA_MY_PROFILE') : ''"
          >
            <feather-icon
              icon="UserIcon"
              size="22"
            />
            <span class="link-text">{{ $t('DEBTIA_MY_PROFILE') }}</span>
          </router-link>
          <a
            href="#"
            class="user-action-link"
            :title="isVerticalMenuCollapsed ? currentLocale.name : ''"
            @click.prevent="toggleLocale"
          >
            <b-img
              :src="currentLocale.img"
              height="14"
              width="22"
              class="locale-flag"
            />
            <span class="link-text">{{ currentLocale.name }}</span>
          </a>
          <a
            href="#"
            class="user-action-link"
            :title="isVerticalMenuCollapsed ? $t('DEBTIA_LOGOUT') : ''"
            @click.prevent="logout"
          >
            <feather-icon
              icon="LogOutIcon"
              size="22"
            />
            <span class="link-text">{{ $t('DEBTIA_LOGOUT') }}</span>
          </a>
          <!-- Collapse toggle as menu item -->
          <a
            href="#"
            class="user-action-link toggle-link d-none d-xl-flex"
            :title="isVerticalMenuCollapsed ? $t('DEBTIA_NAV_EXPAND_MENU') : $t('DEBTIA_NAV_COLLAPSE_MENU')"
            @click.prevent="toggleCollapsed"
          >
            <feather-icon
              :icon="isVerticalMenuCollapsed ? 'ChevronsRightIcon' : 'ChevronsLeftIcon'"
              size="22"
            />
            <span class="link-text">{{ isVerticalMenuCollapsed ? $t('DEBTIA_NAV_EXPAND_MENU') : $t('DEBTIA_NAV_COLLAPSE_MENU') }}</span>
          </a>
        </div>

        <!-- Contact info box - only show when expanded -->
        <div
          v-if="!isVerticalMenuCollapsed"
          class="sidebar-contact-box"
        >
          <div class="contact-title">
            {{ $t('DEBTIA_NEED_HELP') }}
          </div>
          <p class="contact-text">
            {{ $t('DEBTIA_NAV_CONTACT_TEXT') }}
          </p>
          <div class="contact-info">
            <a
              href="tel:+4544224000"
              class="contact-link"
            >
              <feather-icon
                icon="PhoneIcon"
                size="18"
              />
              <span>+45 44 22 40 00</span>
            </a>
            <a
              href="mailto:info@debtia.dk"
              class="contact-link"
            >
              <feather-icon
                icon="MailIcon"
                size="18"
              />
              <span>info@debtia.dk</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import { initialAbility } from '@/libs/acl/config'
import useJwt from '@/auth/jwt/useJwt'
import LogoFull from '@core/layouts/components/LogoFull.vue'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    LogoFull,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      navItems: [],
      debtorsBelongToCompany: localStorage.getItem('debtorsBelongTo') === 'company',
    }
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    /* eslint-disable global-require */
    const locales = [
      {
        locale: 'en',
        img: require('@/assets/images/flags/en.svg'),
        name: 'English',
      },
      {
        locale: 'da',
        img: require('@/assets/images/flags/dk.svg'),
        name: 'Dansk',
      },
    ]
    /* eslint-enable global-require */

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,

      // Locales
      locales,
    }
  },
  computed: {
    currentLocale() {
      return this.locales.find(l => l.locale === this.$i18n.locale) || this.locales[1]
    },
  },
  created() {
    this.getNavItems()
  },
  methods: {
    async getNavItems() {
      const items = await navMenuItems()
      this.navItems = items.filter(obj => obj.title !== 'DEBTIA_DEBTORS' || (obj.title === 'DEBTIA_DEBTORS' && !this.debtorsBelongToCompany))
    },
    toggleLocale() {
      const newLocale = this.$i18n.locale === 'da' ? 'en' : 'da'
      this.$i18n.locale = newLocale
      localStorage.setItem('locale', newLocale)
    },
    logout() {
      this.$i18n.locale = 'da'
      // Remove tokens from localStorage
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      localStorage.removeItem('debtorsBelongTo')
      if (localStorage.getItem('locale')) localStorage.removeItem('locale')
      // Remove userData from localStorage
      localStorage.removeItem('userData')

      // Reset ability
      this.$ability.update(initialAbility)

      // Redirect to login page
      this.$router.push({ name: 'auth-login' })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
